<template>
  <sgdf-sidebar
    class="psb"
    :class="variant === 'rail' ? 'psb--rail' : 'psb--full'"
  >
    <template v-for="(section, index) in visibleSections" :key="section.key">
      <!-- Rail : icônes seules, libellé en tooltip, séparées par section -->
      <template v-if="variant === 'rail'">
        <hr v-if="index > 0" class="psb-divider" />
        <sgdf-sidebar-item
          v-for="item in visibleItems(section)"
          :key="item.key"
          :active="item.key === activeKey"
        >
          <sl-tooltip :content="$t(item.labelKey)" placement="right" hoist>
            <NuxtLink class="psb-link psb-link--rail" :to="item.to">
              <sgdf-icon :name="item.icon" />
            </NuxtLink>
          </sl-tooltip>
        </sgdf-sidebar-item>
      </template>

      <!-- Full : section repliable avec titre + libellés -->
      <sl-details
        v-else
        class="psb-details"
        :open="openSections[section.key] ?? true"
        @sl-after-show="setOpen(section.key, true)"
        @sl-after-hide="setOpen(section.key, false)"
      >
        <span slot="summary" class="psb-summary">{{
          $t(section.titleKey)
        }}</span>
        <sgdf-sidebar-item
          v-for="item in visibleItems(section)"
          :key="item.key"
          :active="item.key === activeKey"
        >
          <NuxtLink class="psb-link" :to="item.to">
            <sgdf-icon :name="item.icon" />
            <span class="psb-label">{{ $t(item.labelKey) }}</span>
          </NuxtLink>
        </sgdf-sidebar-item>
      </sl-details>
    </template>
  </sgdf-sidebar>
</template>

<script setup lang="ts">
import "@sgdf/ui-library/sgdf-sidebar.js";
import "@sgdf/ui-library/sgdf-sidebar-item.js";
import "@sgdf/ui-library/sgdf-icon.js";

defineProps<{
  /** `full` = sections dépliables avec libellés ; `rail` = icônes seules + tooltip. */
  variant: "rail" | "full";
}>();

interface SidebarItem {
  key: string;
  icon: string;
  labelKey: string;
  to: string;
  visible?: boolean;
}
interface SidebarSection {
  key: string;
  titleKey: string;
  items: SidebarItem[];
}

const route = useRoute();
const meetingId = useMeetingId();
const hasModule = useModule();
const { openSections } = useSidebarStore();

const sections = computed<SidebarSection[]>(() => [
  {
    key: "config",
    titleKey: "label.parameters.section.config",
    items: [
      {
        key: "meeting",
        icon: "graduation-cap",
        labelKey: "label.parameters.meeting",
        to: RouteUtils.getMeetingParameters(meetingId.value!),
      },
      {
        key: "participants",
        icon: "person-circle-check",
        labelKey: "label.parameters.participants",
        to: RouteUtils.getMeetingParametersForParticipants(meetingId.value!),
      },
    ],
  },
  {
    key: "prep",
    titleKey: "label.parameters.section.prep",
    items: [
      {
        key: "teams",
        icon: "people-group",
        labelKey: "label.parameters.teams",
        to: RouteUtils.getMeetingParametersForTeams(meetingId.value!),
        visible: hasModule.value("team", meetingId.value),
      },
      {
        key: "dailytasks",
        icon: "list-check",
        labelKey: "label.parameters.daily-tasks",
        to: RouteUtils.getMeetingParametersForDailyTasks(meetingId.value!),
        visible: hasModule.value("dailytask", meetingId.value),
      },
      {
        key: "rooms",
        icon: "people-roof",
        labelKey: "label.parameters.rooms",
        to: RouteUtils.getMeetingParametersForRooms(meetingId.value!),
        visible: hasModule.value("event", meetingId.value),
      },
      {
        key: "planning",
        icon: "calendar",
        labelKey: "label.parameters.planning",
        to: RouteUtils.getMeetingParametersForPlanning(meetingId.value!),
        visible: hasModule.value("event", meetingId.value),
      },
      {
        key: "docs",
        icon: "folder-open",
        labelKey: "label.parameters.docs",
        to: RouteUtils.getMeetingParametersForLinks(meetingId.value!),
        visible: hasModule.value("resource", meetingId.value),
      },
      {
        key: "forms",
        icon: "clipboard-question",
        labelKey: "label.forms",
        to: RouteUtils.getMeetingFormsEdition(meetingId.value!),
        visible: hasModule.value("form", meetingId.value),
      },
      {
        key: "modularity",
        icon: "puzzle-piece",
        labelKey: "label.parameters.modularity",
        to: RouteUtils.getModularEventsEdition(meetingId.value!),
        visible: hasModule.value("modularity", meetingId.value),
      },
      {
        key: "communication",
        icon: "envelope",
        labelKey: "label.parameters.communication",
        to: RouteUtils.getMeetingParametersForCommunication(meetingId.value!),
        visible: hasModule.value("communication", meetingId.value),
      },
    ],
  },
  {
    key: "exports",
    titleKey: "label.parameters.section.exports",
    items: [
      {
        key: "exports",
        icon: "download",
        labelKey: "label.parameters.exports",
        to: RouteUtils.getMeetingParametersForExports(meetingId.value!),
      },
    ],
  },
  {
    key: "admin",
    titleKey: "label.parameters.section.admin",
    items: [
      {
        key: "opinion",
        icon: "feather",
        labelKey: "label.parameters.opinion-review",
        to: RouteUtils.getMeetingParametersForOpinionReview(meetingId.value!),
        // Même module que l'onglet « Avis » de la fiche stagiaire, qui sert à
        // les rédiger : relire des avis qu'on ne peut pas écrire n'a pas de sens.
        visible: hasModule.value("participant_comment", meetingId.value),
      },
      {
        key: "tagada",
        icon: "rotate",
        labelKey: "label.parameters.tagadasync",
        to: RouteUtils.getMeetingParametersForTagada(meetingId.value!),
        visible: hasModule.value("tagada", meetingId.value),
      },
      {
        key: "formers",
        icon: "chalkboard-user",
        labelKey: "label.parameters.formers",
        to: RouteUtils.getMeetingParametersForFormers(meetingId.value!),
      },
    ],
  },
]);

const visibleItems = (section: SidebarSection) =>
  section.items.filter((item) => item.visible !== false);

// N'affiche pas une section (ni son séparateur) si tous ses items sont masqués.
const visibleSections = computed(() =>
  sections.value.filter((section) => visibleItems(section).length > 0),
);

/** Item actif = celui dont le `to` est le plus long préfixe de la route courante. */
const activeKey = computed(() => {
  const path = route.path;
  const match = sections.value
    .flatMap(visibleItems)
    .filter((item) => path === item.to || path.startsWith(item.to + "/"))
    .sort((a, b) => b.to.length - a.to.length)[0];
  return match?.key;
});

const setOpen = (key: string, open: boolean) => {
  openSections.value = { ...openSections.value, [key]: open };
};

// Ouvre automatiquement la section contenant l'item actif.
watch(
  activeKey,
  (key) => {
    if (!key) return;
    const section = sections.value.find((s) =>
      s.items.some((item) => item.key === key),
    );
    if (section && openSections.value[section.key] === false) {
      setOpen(section.key, true);
    }
  },
  { immediate: true },
);
</script>

<style scoped>
/* Le style des items (padding, radius, hover, actif) vient de sgdf-sidebar-item. */
.psb-link {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: inherit;
  text-decoration: none;
}

.psb-link--rail {
  justify-content: center;
  gap: 0;
}

.psb-label {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* --- Section repliable (mode full) --- */
.psb-summary {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-weight: 700;
  color: var(--sgdf-color-gray-500, #6b7280);
}

.psb-details::part(base) {
  border: none;
  background: transparent;
  border-radius: 0;
}

.psb-details::part(header) {
  padding: 0.5rem 0.625rem;
}

.psb-details::part(content) {
  padding: 0;
}

/* --- Rail (icônes seules) --- */
.psb-divider {
  border: none;
  border-top: 1px solid var(--sgdf-color-gray-200, #e5e7eb);
  margin: 0.5rem 0.25rem;
}
</style>
